import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Activity, 
  RefreshCw, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  BarChart3,
  Filter,
  Crosshair,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const AdminScannerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runningFullScan, setRunningFullScan] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [sniperData, setSniperData] = useState(null);

  const fetchScannerData = useCallback(async () => {
    try {
      const [statusRes, sniperRes] = await Promise.all([
        axios.get('/admin/ai-signals/status').catch(() => ({ data: null })),
        axios.get('/admin/sniper/watchlist').catch(() => ({ data: null }))
      ]);
      setSystemStatus(statusRes.data?.auto_scanner);
      setSniperData(sniperRes.data);
    } catch (error) {
      console.error('Scanner data fetch error:', error);
      toast.error('Tarama verileri yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchScannerData();
    // Auto refresh every 60 seconds
    const interval = setInterval(fetchScannerData, 60000);
    return () => clearInterval(interval);
  }, [fetchScannerData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchScannerData();
  };

  const handleRunFullScan = async () => {
    if (runningFullScan) return;
    
    setRunningFullScan(true);
    try {
      // Trigger Sniper auto-scanner (updates watchlist data)
      const sniperResponse = await axios.post('/admin/sniper/scan/trigger');
      toast.success(`Sniper taraması tamamlandı: ${sniperResponse.data.result?.total_scanned || 0} coin tarandı`);
      fetchScannerData();
    } catch (error) {
      toast.error('Tarama başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRunningFullScan(false);
    }
  };

 

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-900 min-h-screen">
      {/* API Connections Status Banner */}
      <div className="p-3 rounded-lg border bg-slate-800/50 border-slate-700">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* KuCoin - Primary Data Source */}
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm text-white">KuCoin API</span>
              <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">Ana Veri</Badge>
            </div>
            
            {/* OKX - Fallback */}
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm text-slate-300">OKX API</span>
              <Badge className="bg-slate-500/20 text-slate-400 text-xs">Yedek</Badge>
            </div>
            
            {/* OpenAI GPT-4o */}
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm text-slate-300">GPT-4o</span>
              <Badge className="bg-purple-500/20 text-purple-400 text-xs">AI</Badge>
            </div>
            
            {/* Telegram */}
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm text-slate-300">Telegram</span>
              <Badge className="bg-blue-500/20 text-blue-400 text-xs">Bildirim</Badge>
            </div>
          </div>
          
          <div className="text-xs text-slate-500">
            Veri: KuCoin 4H + 15m
          </div>
        </div>
      </div>

      {/* 🎯 SNIPER SYSTEM STATUS (v3.0 - Simplified) */}
      {systemStatus && (
        <Card className="bg-gradient-to-r from-slate-800/80 to-orange-900/30 border-orange-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Crosshair className="w-5 h-5 text-orange-400" />
              🎯 Sniper Tarama Sistemi v3.0
              <Badge className={systemStatus.is_running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                {systemStatus.is_running ? 'ÇALIŞIYOR' : 'DURDU'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Sniper Config */}
              <div className="bg-slate-900/50 rounded-lg p-4 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-orange-400" />
                  <span className="font-semibold text-white">Sniper Engine</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tarama:</span>
                    <span className="text-cyan-400">15 Coin / 15 dk</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Veri:</span>
                    <span className="text-white">KuCoin 4H + 15m</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Min Skor:</span>
                    <span className="text-orange-400">{systemStatus.sniper?.min_score || 60}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Expiry:</span>
                    <span className="text-yellow-400">8 saat</span>
                  </div>
                </div>
              </div>

              {/* Last Scan Stats */}
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  <span className="font-semibold text-white">Son Tarama</span>
                </div>
                {systemStatus.sniper?.last_scan_summary ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Taranan:</span>
                      <span className="text-white">{systemStatus.sniper.last_scan_summary.total_scanned || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Geçen:</span>
                      <span className="text-green-400">{systemStatus.sniper.last_scan_summary.sniper_passed || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Yayınlanan:</span>
                      <span className="text-cyan-400">{systemStatus.sniper.last_scan_summary.signals_published || 0}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Henüz tarama yok</p>
                )}
              </div>

              {/* Next Run */}
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="font-semibold text-white">Zamanlama</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Sonraki:</span>
                    <span className="text-yellow-400">
                      {systemStatus.sniper?.next_run 
                        ? new Date(systemStatus.sniper.next_run).toLocaleTimeString('tr-TR')
                        : 'Her :00, :15, :30, :45'}
                    </span>
                  </div>
                  {systemStatus.sniper?.last_scan_time && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Son:</span>
                      <span className="text-slate-300">
                        {new Date(systemStatus.sniper.last_scan_time).toLocaleTimeString('tr-TR')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Architecture Info */}
            <div className="mt-4 p-3 bg-slate-900/30 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span>
                  <strong className="text-orange-400">Sniper karar verir</strong> (rule-based, deterministik) →{' '}
                  <strong className="text-purple-400">GPT yorum ekler</strong> (opsiyonel, non-blocking)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crosshair className="w-6 h-6 text-orange-400" />
            Sniper Tarama Paneli v3.0
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            15 Coin / 15 dk | Rule-Based Karar | GPT Yorum
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-slate-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button
            size="sm"
            onClick={handleRunFullScan}
            disabled={runningFullScan}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Zap className={`w-4 h-4 mr-2 ${runningFullScan ? 'animate-pulse' : ''}`} />
            {runningFullScan ? 'Taranıyor...' : 'Manuel Tarama'}
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs">Taranan Coin</p>
                <p className="text-2xl font-bold text-white">{sniperData?.sniper_scan?.total_scanned || 0}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs">AI Adayı</p>
                <p className="text-2xl font-bold text-cyan-400">{sniperData?.sniper_scan?.sniper_passed || 0}</p>
              </div>
              <Target className="w-8 h-8 text-cyan-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs">Tarama Süresi</p>
                <p className="text-2xl font-bold text-white">-</p>
              </div>
              <Clock className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs">Min R:R</p>
                <p className="text-2xl font-bold text-white">1.3</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs">Cooldown</p>
                <p className="text-2xl font-bold text-white">6h</p>
              </div>
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 🎯 SNIPER SCAN RESULTS */}
      {sniperData?.sniper_scan && (
        <Card className="bg-slate-800/50 border-orange-500/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Crosshair className="w-5 h-5 text-orange-400" />
                🎯 Sniper Tarama Sonuçları
              </CardTitle>
              {sniperData.sniper_scan.last_scan_time && (
                <span className="text-xs text-slate-400">
                  Son: {new Date(sniperData.sniper_scan.last_scan_time).toLocaleTimeString('tr-TR')}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Stats Row - Daha net metrikler */}
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-400">{sniperData.sniper_scan.total_scanned}</p>
                <p className="text-xs text-slate-400">Taranan</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-yellow-400">{sniperData.sniper_scan.sniper_passed}</p>
                <p className="text-xs text-slate-400">Valid (≥60)</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-400">
                  {sniperData.sniper_scan.results?.filter(r => r.sniper_score >= 70).length || 0}
                </p>
                <p className="text-xs text-slate-400">A/A+ (≥70)</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-400">{sniperData.sniper_scan.gpt_called || 0}</p>
                <p className="text-xs text-slate-400">GPT Yorum</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-cyan-400">{sniperData.sniper_scan.signals_published}</p>
                <p className="text-xs text-slate-400">Aktif Sinyal</p>
              </div>
            </div>

            {/* Grade-based publish info */}
            <div className="mb-4 p-2 bg-slate-900/30 rounded text-xs text-slate-400">
              <span className="text-green-400">A/A+ (≥70)</span>: Otomatik yayın | 
              <span className="text-yellow-400 ml-2">B (60-69)</span>: Ayara bağlı | 
              <span className="text-red-400 ml-2">C (&lt;60)</span>: Yayın yok |
              <span className="text-red-400 ml-2">REJECT (0)</span>: Hard gate (EMA200 veya Entry&gt;Price)
            </div>

            {/* Results Table - YENİ: Anlık vs Aktif Sinyal Ayrımı */}
            {sniperData.sniper_scan.results?.length > 0 ? (
              <div className="overflow-x-auto">
                {/* Bilgilendirme Tooltip */}
                <div className="mb-3 p-2 bg-slate-900/50 rounded text-xs text-slate-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  <span>
                    <strong className="text-white">Anlık Skor:</strong> Son 15dk tarama sonucu | 
                    <strong className="text-cyan-400 ml-2">DB Aktif Sinyal:</strong> Geçmişte yayınlanmış ve hâlâ açık olan sinyal |
                    <strong className="text-red-400 ml-2">REJECT:</strong> Badge'e hover ile sebep görüntülenir
                  </span>
                </div>
                
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-700">
                      <th className="pb-2 font-medium">Coin</th>
                      <th className="pb-2 font-medium text-center">Anlık Skor</th>
                      <th className="pb-2 font-medium text-center">Anlık Grade</th>
                      <th className="pb-2 font-medium text-center">Sweep</th>
                      <th className="pb-2 font-medium text-center">Reclaim</th>
                      <th className="pb-2 font-medium text-center">DB Aktif?</th>
                      <th className="pb-2 font-medium text-center">Aktif Sinyal Skoru</th>
                      <th className="pb-2 font-medium text-center">Açılış Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sniperData.sniper_scan.results.map((result) => {
                      const isAGrade = result.sniper_score >= 70;
                      const isBGrade = result.sniper_score >= 60 && result.sniper_score < 70;
                      const hasActiveSignal = result.has_active_signal || result.published;
                      
                      // Sweep type badge styling
                      const sweepBadge = {
                        'STRONG_RECLAIM': { bg: 'bg-green-500/20', text: 'text-green-400', label: '💪 Güçlü' },
                        'LIQUIDITY_GRAB': { bg: 'bg-orange-500/20', text: 'text-orange-400', label: '🎯 Standart' },
                        'WEAK_RECLAIM': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: '⚠️ Zayıf' },
                      }[result.sweep_type] || null;
                      
                      // Format date for display
                      const formatDate = (dateStr) => {
                        if (!dateStr) return '-';
                        try {
                          return new Date(dateStr).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        } catch {
                          return '-';
                        }
                      };
                      
                      return (
                        <tr key={result.symbol} className={`border-b border-slate-700/50 ${hasActiveSignal ? 'bg-cyan-500/5' : isAGrade ? 'bg-green-500/5' : isBGrade ? 'bg-yellow-500/5' : ''}`}>
                          <td className="py-2 font-medium text-white">{result.symbol}</td>
                          
                          {/* ANLIK SKOR - Son tarama */}
                          <td className="py-2 text-center">
                            {result.sniper_score > 0 ? (
                              <span className={`font-bold ${
                                result.sniper_score >= 70 ? 'text-green-400' :
                                result.sniper_score >= 60 ? 'text-yellow-400' :
                                'text-slate-500'
                              }`}>
                                {result.sniper_score}
                              </span>
                            ) : (
                              <span 
                                className="text-slate-600 cursor-help" 
                                title={result.rejection_reason || 'Reject'}
                              >
                                0
                              </span>
                            )}
                          </td>
                          
                          {/* ANLIK GRADE */}
                          <td className="py-2 text-center">
                            <Badge 
                              className={
                                result.sniper_grade === 'A+' ? 'bg-green-500/20 text-green-400' :
                                result.sniper_grade === 'A' ? 'bg-green-500/20 text-green-400' :
                                result.sniper_grade === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                                result.sniper_grade === 'C' ? 'bg-orange-500/20 text-orange-400' :
                                'bg-red-500/20 text-red-400'
                              }
                              title={result.rejection_reason || ''}
                            >
                              {result.sniper_grade}
                            </Badge>
                          </td>
                          
                          {/* SWEEP */}
                          <td className="py-2 text-center">
                            {result.sweep_detected ? (
                              sweepBadge ? (
                                <Badge className={`${sweepBadge.bg} ${sweepBadge.text}`}>
                                  {sweepBadge.label}
                                </Badge>
                              ) : (
                                <Badge className="bg-orange-500/20 text-orange-400">✓</Badge>
                              )
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                          
                          {/* RECLAIM */}
                          <td className="py-2 text-center">
                            {result.reclaim_strength ? (
                              <span className={`font-medium ${
                                result.reclaim_strength >= 70 ? 'text-green-400' :
                                result.reclaim_strength >= 50 ? 'text-yellow-400' :
                                'text-orange-400'
                              }`}>
                                %{result.reclaim_strength.toFixed(0)}
                              </span>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                          
                          {/* DB AKTİF SİNYAL VAR MI? - YENİ NET ROZET */}
                          <td className="py-2 text-center">
                            {hasActiveSignal ? (
                              <Badge className="bg-cyan-500/20 text-cyan-400">
                                ✅ Var
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-700/50 text-slate-500">
                                ❌ Yok
                              </Badge>
                            )}
                          </td>
                          
                          {/* AKTİF SİNYALİN ORİJİNAL SKORU - YENİ */}
                          <td className="py-2 text-center">
                            {hasActiveSignal && result.active_signal_score ? (
                              <span className="font-bold text-cyan-400">
                                {result.active_signal_score}
                                {result.active_signal_grade && (
                                  <span className="text-xs text-slate-400 ml-1">({result.active_signal_grade})</span>
                                )}
                              </span>
                            ) : hasActiveSignal ? (
                              <span className="text-slate-400 text-xs">Eski sinyal</span>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                          
                          {/* AKTİF SİNYAL TARİHİ - YENİ */}
                          <td className="py-2 text-center">
                            {hasActiveSignal && result.active_signal_date ? (
                              <span className="text-xs text-slate-300">
                                {formatDate(result.active_signal_date)}
                              </span>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400">
                <Crosshair className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                <p>Henüz Sniper tarama sonucu yok</p>
                <p className="text-xs text-slate-500">İlk tarama 15 dk içinde gerçekleşecek</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminScannerDashboard;
